import ShippingClient from "../api/ShippingClient";
import { CartContents } from "../contexts/CartContext";
import { computeTax, taxableSubtotal } from "./tax";


class CartUtils {
    cart: CartContents;
    discountedSubtotal: number = 0;
    subtotal: number;
    /** Part of `subtotal` that HST applies to. Equal to `subtotal`
     *  unless the cart holds a tax-exempt item. */
    taxableSubtotal: number;
    shipping: number | undefined;

    constructor(cart: CartContents) {
        this.cart = cart;

        const items: ICartItem[] = Object.values(cart.items);

        this.subtotal = items.reduce((subtotal, item) => subtotal + (item.count * (item.item.price ? item.item.price : 0)), 0);

        this.taxableSubtotal = taxableSubtotal(items);

        this.discountedSubtotal = cart.discountAmount ? this.subtotal - cart.discountAmount : this.subtotal;
    }

    getShipping = async(): Promise<number|undefined> => {
        if (!this.shipping) {
            if (this.cart.shippingMethod) {
                const shippingDetails = await ShippingClient.getShipping(this.cart.shippingMethod, this.cart, this.discountedSubtotal);
                if (shippingDetails !== undefined) {
                    if (this.discountedSubtotal) {
                        if (shippingDetails.freeShippingThreshold && this.discountedSubtotal >= shippingDetails.freeShippingThreshold) {
                            this.shipping = 0;
                        }
                        else {
                            this.shipping = shippingDetails.price;
                            if (this.shipping < 0) {
                                this.shipping = undefined;
                            }
                        }
                    }
                }
            }
        }
        return this.shipping;
    }
      
    getTotal = async (): Promise<number|undefined> => {
        const shipping = await this.getShipping();
        if (shipping === undefined) {
            return undefined;
        }
        // Points redemption is applied to the pre-tax subtotal, same as
        // a discount code — taxes are computed AFTER the redemption, and
        // both are spread pro rata across taxable and exempt lines.
        // Mirrors the server-side computation in checkout().
        const redemption = Math.max(0, this.cart.pointsToUse ?? 0);
        const discount = (this.cart.discountAmount ?? 0) + redemption;
        // Shipping is taxable in NS, so it joins both bases.
        const taxes = computeTax(
            this.taxableSubtotal + shipping,
            this.subtotal + shipping,
            discount,
        );
        const total = Math.max(0, this.subtotal + shipping - discount) + taxes;
        return total;
    }

    getEnvironment = (): string => {
        // Read off cardholderAddress (the name on the card about to be
        // charged), NOT deliveryAddress — the storefront pins delivery
        // addresses across sessions, so editing customer + cardholder
        // back to real values leaves a stale "TEST" sitting in delivery
        // and flips this to TEST even though the user's intent is PROD.
        const cardholderFirst = this.cart.cardholderAddress?.firstName ?? '';
        const deliveryFirst = this.cart.deliveryAddress?.firstName ?? '';
        const customerFirst = this.cart.customerAddress?.firstName ?? '';
        const verdict = cardholderFirst.toUpperCase() === "TEST" ? "TEST" : "PROD";
        // Temporary debug — leave in until the test-mode trigger is
        // confirmed stable across saved-address autofill scenarios.
        // Remove these console.log calls once the symptom is gone.
        console.log('[CartUtils.getEnvironment]', {
            verdict,
            cardholderFirst,
            deliveryFirst,
            customerFirst,
        });
        return verdict;
    }
}

export default CartUtils;